import i18next from "src/i18n/config";

import { Category } from "@admin/api/event";
import { EventReport } from "@dashboard/devices/types";
import { Typography } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";
import { ColumnFiltersState, createColumnHelper } from "@tanstack/react-table";

const columnHelper = createColumnHelper<EventReport>();

export type FormValues = {
    workspaceId?: string;
    groupId?: string;
    entityId?: string;
};

export const columns = [
    columnHelper.display({
        id: "collapse",
        header: "",
        cell: () => null,
    }),
    columnHelper.accessor("created", {
        id: "created",
        header: i18next.t("sidepanels:devicePanel.table.header.date"),
        enableSorting: true,
        cell: (info) => {
            const dateTime = TimeService.getLocalDate(info.getValue(), "ISO", "dateTime");
            const date = TimeService.getLocalDate(info.getValue(), "ISO", "date");
            return (
                <MinimalTableCell>
                    <Tooltip title={dateTime}>
                        <Typography>{date}</Typography>
                    </Tooltip>
                </MinimalTableCell>
            );
        },
    }),
    columnHelper.accessor("level", {
        id: "level",
        header: () => i18next.t("common:label.type"),
        enableSorting: false,
        filterFn: (row, _columnId, filterValue: string[]) => {
            return filterValue.includes(String(row.original.level));
        },
        cell: (info) => {
            const level =
                info.row.original.level === "warning"
                    ? i18next.t("common:table.filter.warning")
                    : i18next.t("common:table.filter.notice");
            return (
                <MinimalTableCell>
                    <Typography variant="body1">{level}</Typography>
                </MinimalTableCell>
            );
        },
    }),
    columnHelper.accessor("workspaceId", {
        id: "workspaceId",
        header: i18next.t("common:label.workspaceId"),
        enableSorting: false,
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("groupId", {
        id: "groupId",
        header: i18next.t("common:label.groupId"),
        enableSorting: false,
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("eventTypeName", {
        id: "event",
        header: i18next.t("common:label.event"),
        enableSorting: false,
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("entityId", {
        id: "entityId",
        header: i18next.t("common:label.entityId"),
        enableSorting: false,
        cell: (info) => (
            <MinimalTableCell>
                <Typography>{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
];

export function getEventLevel(columnFilters: ColumnFiltersState): number[] | undefined {
    const levelFilter = columnFilters.find((f) => f.id === "eventLevel");
    const warningFilterActive = Array.isArray(levelFilter?.value) && !!levelFilter?.value.includes("warning");
    const noticeFilterActive = Array.isArray(levelFilter?.value) && !!levelFilter?.value.includes("notice");

    let level;

    if (warningFilterActive && noticeFilterActive) {
        level = undefined;
    } else if (warningFilterActive) {
        level = 1;
    } else if (noticeFilterActive) {
        level = 0;
    }

    return level === undefined ? undefined : [level];
}

export function getEventCategory(columnFilters: ColumnFiltersState): Category[] | undefined {
    const categoryFilter = columnFilters.find((f) => f.id === "category")?.value as Category[] | undefined;
    return categoryFilter?.length ? categoryFilter : undefined;
}

export function parseIdList(value?: string): string[] | undefined {
    const values = (value ?? "")
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    return values.length ? values : undefined;
}

export function parseNumericIdList(value?: string): number[] | undefined {
    const numbers = parseIdList(value)
        ?.map(Number)
        .filter((n) => !Number.isNaN(n));
    return numbers?.length ? numbers : undefined;
}

export function isFilterApplied(filterState: FormValues): boolean {
    return Boolean(filterState.workspaceId || filterState.groupId || filterState.entityId);
}
